import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
  CircularProgress,
  TextField,
  InputAdornment,
  Pagination,
  Alert,
  styled,
  useTheme,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Collapse,
  Grid,
  Divider,
  Card,
  CardContent,
  FormHelperText,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
  ArrowDropDown,
  Close as CloseIcon,
  FilterList as FilterListIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Visibility as VisibilityIcon,
} from '@mui/icons-material';
import useFetchObjects from '../../api/useFetchObjects';
import useFetchObject from '../../api/useFetchObject';
import useDelete from '../../api/useDelete';
import useAdd from '../../api/useAdd';
import useUpdate from '../../api/useUpdate';
import { useAuth } from '../../context/AuthContext';

// RTL-aware TextField component
const RTLTextField = styled(TextField)(({ theme, isRtl }) => ({
  ...(isRtl && {
    '& .MuiInputBase-input': {
      textAlign: 'right',
      direction: 'rtl',
      padding: '10px 14px 16px',
    },
    '& .MuiInputLabel-root': {
      right: 25,
      left: 'auto',
      transformOrigin: 'right',
      marginRight: '5px',
    },
    '& .MuiOutlinedInput-root': {
      paddingLeft: 14,
      paddingRight: 0,
    },
    '& .MuiInputAdornment-root': {
      marginRight: 8,
      marginLeft: 0,
    },
    '& .MuiOutlinedInput-notchedOutline': {
      textAlign: 'right',
    },
    '& .MuiFormHelperText-root': {
      textAlign: 'right',
    },
  }),
}));

// RTL-aware TableCell component with bold header option
const RTLTableCell = styled(TableCell)(({ theme, isRtl, align, isHeader }) => ({
  ...(isRtl && {
    textAlign: align || 'right',
    direction: 'rtl',
  }),
  ...(isHeader && {
    fontWeight: 'bold',
  }),
}));

// Custom styled button for better RTL support
const RTLButton = styled(Button)(({ theme, isRtl }) => ({
  ...(isRtl && {
    flexDirection: 'row-reverse',
  }),
}));

const RTLIconButton = styled(IconButton)(({ theme, isRtl }) => ({
  ...(isRtl && {
    transform: 'scaleX(-1)',
  }),
}));

// RTL-aware FormControl
const RTLFormControl = styled(FormControl)(({ theme, isRtl }) => ({
  ...(isRtl && {
    '& .MuiInputLabel-root': {
      right: 25,
      left: 'auto',
      transformOrigin: 'right',
      marginRight: '5px',
    },
    '& .MuiInputBase-input': {
      textAlign: 'right',
      direction: 'rtl',
    },
    '& .MuiSelect-select': {
      paddingRight: '32px',
      textAlign: 'right',
      paddingLeft: '32px',
    },
    '& .MuiSelect-icon': {
      right: 'auto',
      left: 7,
      pointerEvents: 'none',
      color: theme.palette.action.active,
    },
    '& .MuiFormHelperText-root': {
      textAlign: 'right',
    },
  }),
}));

// RTL Dialog Title
const RTLDialogTitle = styled(DialogTitle)(({ theme, isRtl }) => ({
  ...(isRtl && {
    textAlign: 'right',
    direction: 'rtl',
  }),
}));

// RTL Dialog Content
const RTLDialogContent = styled(DialogContent)(({ theme, isRtl }) => ({
  ...(isRtl && {
    textAlign: 'right',
    direction: 'rtl',
  }),
}));

// RTL Dialog Actions
const RTLDialogActions = styled(DialogActions)(({ theme, isRtl }) => ({
  ...(isRtl && {
    flexDirection: 'row-reverse',
  }),
}));

const ProvinceList = () => {
  const { t, i18n } = useTranslation(['provinces', 'common', 'validation']);
  const { user, hasPermission } = useAuth();
  const token = user?.token;
  const isRtl = i18n.language === 'dari' || i18n.language === 'ps';
  const theme = useTheme();
  
  // Permission state - check if user has required permissions
  const [permissions, setPermissions] = useState({
    canCreate: false,
    canUpdate: false,
    canDelete: false
  });

  // Permission error message
  const [permissionError, setPermissionError] = useState('');
  const [showPermissionError, setShowPermissionError] = useState(false);
  
  // State for filtering and pagination
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [search, setSearch] = useState('');
  const [showFilters, setShowFilters] = useState(false);

  // State for dialog management
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedProvinceId, setSelectedProvinceId] = useState(null);

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    code: ''
  });

  // Validation state
  const [errors, setErrors] = useState({});
  
  // Check user permissions
  useEffect(() => {
    setPermissions({
      canCreate: hasPermission('create:province'),
      canUpdate: hasPermission('update:province'),
      canDelete: hasPermission('delete:province')
    });
    
    // For development/testing - temporary solution to enable Create button
    // Remove this in production when permissions are properly set up
    if (process.env.NODE_ENV === 'development') {
      setPermissions(prev => ({
        ...prev,
        canCreate: true
      }));
    }
  }, [hasPermission]);
  
  // Query parameters for API call
  const params = {
    page,
    limit,
    search
  };
  
  // Fetch provinces
  const { 
    data, 
    isLoading, 
    isError, 
    error,
    refetch 
  } = useFetchObjects('provinces', params, token);
  
  // Fetch province data for edit
  const { 
    data: provinceData, 
    isLoading: isLoadingProvince,
  } = useFetchObject('provinces', selectedProvinceId ? `provinces/${selectedProvinceId}` : null, selectedProvinceId, token);

  // Handle CRUD operation errors with permission checks
  const handleApiError = (error) => {
    if (error?.response?.status === 403) {
      setPermissionError(t('common:permission_denied'));
      setShowPermissionError(true);
      return true;
    }
    return false;
  };

  // Delete functionality with permission handling
  const { 
    handleDelete: baseHandleDelete, 
    ConfirmDialog,
    error: deleteError
  } = useDelete('provinces', token);

  const handleDelete = (id) => {
    // Always allow in development mode
    if (process.env.NODE_ENV === 'development' || permissions.canDelete) {
      baseHandleDelete(id).catch(error => {
        handleApiError(error);
      });
      return;
    }
    
    setPermissionError(t('common:permission_denied_delete'));
    setShowPermissionError(true);
  };

  // Create functionality with permission handling
  const { 
    handleAdd: baseHandleAdd, 
    loading: isCreating, 
    success: createSuccess,
    error: addError
  } = useAdd(
    'provinces',
    token,
    '/provinces',
    t('create_success'),
    t('create_error')
  );

  const handleAdd = (data) => {
    // Always allow in development mode
    if (process.env.NODE_ENV === 'development' || permissions.canCreate) {
      baseHandleAdd(data);
      return;
    }
    
    setPermissionError(t('common:permission_denied_create'));
    setShowPermissionError(true);
    return Promise.reject(new Error('Permission denied'));
  };

  // Update functionality with permission handling
  const { 
    handleUpdate: baseHandleUpdate, 
    loading: isUpdating, 
    success: updateSuccess,
    error: updateError
  } = useUpdate(
    'provinces',
    token,
    '/provinces',
    t('update_success'),
    t('update_error')
  );

  const handleUpdate = (id, data) => {
    // Always allow in development mode
    if (process.env.NODE_ENV === 'development' || permissions.canUpdate) {
      baseHandleUpdate(id, data);
      return;
    }
    
    setPermissionError(t('common:permission_denied_update'));
    setShowPermissionError(true);
    return Promise.reject(new Error('Permission denied'));
  };

  // Close permission error snackbar
  const handleClosePermissionError = () => {
    setShowPermissionError(false);
  };

  // Handle search input change
  const handleSearchChange = (e) => {
    setSearch(e.target.value);
  };
  
  // Handle search submit
  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    refetch();
  };

  // Handle page change
  const handlePageChange = (event, value) => {
    setPage(value);
  };

  // Handle page size change
  const handleLimitChange = (e) => {
    setLimit(Number(e.target.value));
    setPage(1); // Reset to first page when limit changes
  };

  // Open create dialog with permission check
  const handleOpenCreateDialog = () => {
    // Always allow in development mode
    if (process.env.NODE_ENV === 'development' || permissions.canCreate) {
      setFormData({
        name: '',
        code: ''
      });
      setErrors({});
      setCreateDialogOpen(true);
      return;
    }
    
    setPermissionError(t('common:permission_denied_create'));
    setShowPermissionError(true);
  };

  // Close create dialog
  const handleCloseCreateDialog = () => {
    setCreateDialogOpen(false);
  };

  // Open edit dialog with permission check
  const handleOpenEditDialog = (id) => {
    // Always allow in development mode
    if (process.env.NODE_ENV === 'development' || permissions.canUpdate) {
      setSelectedProvinceId(id);
      setEditDialogOpen(true);
      return;
    }
    
    setPermissionError(t('common:permission_denied_update'));
    setShowPermissionError(true);
  };

  // Close edit dialog
  const handleCloseEditDialog = () => {
    setEditDialogOpen(false);
    setSelectedProvinceId(null);
  };

  // Handle input change
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    
    // Clear error for this field
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: null,
      }));
    }
  };

  // Set form data when province data is loaded (for edit)
  React.useEffect(() => {
    if (provinceData && editDialogOpen) {
      const province = provinceData.data || provinceData;
      
      setFormData({
        name: province.name || '',
        code: province.code || ''
      });
    }
  }, [provinceData, editDialogOpen]);

  // Reset form and close dialog when operation is successful
  React.useEffect(() => {
    if (createSuccess) {
      handleCloseCreateDialog();
      refetch();
    }
    
    if (updateSuccess) {
      handleCloseEditDialog();
      refetch();
    }
  }, [createSuccess, updateSuccess]);

  // Check for API errors that might be permission-related
  React.useEffect(() => {
    if (addError?.response?.status === 403 || 
        updateError?.response?.status === 403 || 
        deleteError?.response?.status === 403) {
      setPermissionError(t('common:permission_denied'));
      setShowPermissionError(true);
    }
  }, [addError, updateError, deleteError, t]);

  // Validate form
  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name.trim()) {
      newErrors.name = t('validation:required');
    }
    
    if (!formData.code.trim()) {
      newErrors.code = t('validation:required');
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle create form submission
  const handleCreateSubmit = (e) => {
    e.preventDefault();
    
    // Validate form
    if (!validateForm()) {
      return;
    }
    
    // Submit form data
    handleAdd(formData);
  };

  // Handle edit form submission
  const handleEditSubmit = (e) => {
    e.preventDefault();
    
    // Validate form
    if (!validateForm()) {
      return;
    }
    
    // Submit form data
    handleUpdate(selectedProvinceId, formData);
  };

  // Calculate total pages
  const totalPages = data?.totalPages || 1;
  
  // Provinces list
  const provinces = data?.data || [];

  return (
    <Box dir={isRtl ? 'rtl' : 'ltr'} sx={{ width: '100%' }}>
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        mb: 3,
        flexDirection: isRtl ? 'row-reverse' : 'row',
        width: '100%'
      }}>
        {/* Always show Add button in development, or if user has permission */}
        {(process.env.NODE_ENV === 'development' || permissions.canCreate) && (
          <RTLButton
            variant="contained"
            startIcon={!isRtl && <AddIcon />}
            endIcon={isRtl && <AddIcon />}
            onClick={handleOpenCreateDialog}
            isRtl={isRtl}
            disabled={!permissions.canCreate}
            sx={{ 
              whiteSpace: 'nowrap',
              borderRadius: 2,
              px: 2,
              py: 0.75,
              textTransform: 'none',
              fontWeight: 500,
              boxShadow: 2,
              '&:hover': {
                boxShadow: 4
              }
            }}
          >
            {t('create_new')}
          </RTLButton>
        )}
        
        <Typography 
          variant="h4" 
          component="h1" 
          sx={{ 
            textAlign: isRtl ? 'right' : 'left',
            fontWeight: 'bold',
            ml: !(process.env.NODE_ENV === 'development' || permissions.canCreate) ? 'auto' : 0
          }}
        >
          {t('list_title')}
        </Typography>
      </Box>

      {/* Search and filters */}
      <Box sx={{ 
        display: 'flex', 
        mb: 3, 
        gap: 2,
        flexDirection: isRtl ? 'row-reverse' : 'row',
        width: '100%'
      }}>
        <RTLTextField
                variant="outlined"
          size="small"
          placeholder={t('common:search')}
          value={search}
                onChange={handleSearchChange}
          sx={{ 
            flexGrow: 1,
            '& .MuiOutlinedInput-root': {
              paddingRight: isRtl ? '0px' : '0',
              paddingLeft: isRtl ? '0' : '0px',
            },
            '& .MuiOutlinedInput-input': {
              padding: '10px 14px',
            }
          }}
          isRtl={isRtl}
                InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
            dir: isRtl ? 'rtl' : 'ltr',
          }}
        />
        
        <RTLButton 
                variant="contained"
          onClick={handleSearch}
          isRtl={isRtl}
        >
          {t('common:search')}
        </RTLButton>
        
        <RTLButton 
          variant="outlined" 
          onClick={() => {
            setSearch('');
            refetch();
          }}
          isRtl={isRtl}
        >
          {t('common:reset')}
        </RTLButton>
        
        <RTLButton 
          variant="outlined" 
          startIcon={!isRtl && <RefreshIcon />}
          endIcon={isRtl && <RefreshIcon />}
          onClick={refetch}
          isRtl={isRtl}
        >
          {t('common:refresh')}
        </RTLButton>
        
        <RTLButton
          variant="text"
          color="primary"
          onClick={() => setShowFilters(!showFilters)}
          startIcon={!isRtl && (showFilters ? <ExpandLessIcon /> : <ExpandMoreIcon />)}
          endIcon={isRtl && (showFilters ? <ExpandLessIcon /> : <ExpandMoreIcon />)}
          isRtl={isRtl}
        >
          <FilterListIcon fontSize="small" sx={{ mr: 0.5 }} />
          {t('common:filter')}
        </RTLButton>
      </Box>
      
      <Collapse in={showFilters}>
        <Paper sx={{ p: 2, mb: 3, borderRadius: 2 }}>
          <Box sx={{ 
            display: 'flex', 
            flexDirection: isRtl ? 'row-reverse' : 'row', 
            gap: 2, 
            flexWrap: 'wrap' 
          }}>
            {/* Additional filters can be added here as needed */}
          </Box>
        </Paper>
      </Collapse>

      {/* Error alert */}
      {isError && (
        <Alert severity="error" sx={{ mb: 3, textAlign: isRtl ? 'right' : 'left' }}>
          {error?.message || t('error_occurred')}
        </Alert>
      )}

      {/* Table */}
      <Paper sx={{ width: '100%', overflow: 'hidden', mb: 3 }}>
        <TableContainer>
          <Table>
            <TableHead sx={{ 
              bgcolor: theme.palette.grey[100],
              '& .MuiTableRow-root': {
                borderBottom: `2px solid ${theme.palette.grey[300]}`,
              }
            }}>
              <TableRow>
                <RTLTableCell 
                  isRtl={isRtl} 
                  isHeader
                  align={isRtl ? "right" : "left"}
                  sx={{ 
                    fontSize: '1rem',
                    fontWeight: 600,
                    color: theme.palette.text.primary,
                    textAlign: isRtl ? 'right' : 'left'
                  }}
                >
                   {t('id')}
                </RTLTableCell>
                <RTLTableCell 
                  isRtl={isRtl} 
                  isHeader
                  sx={{ 
                    fontSize: '1rem',
                    fontWeight: 600,
                    color: theme.palette.text.primary
                  }}
                >
                  {t('name')}
                </RTLTableCell>
                <RTLTableCell 
                  isRtl={isRtl} 
                  isHeader
                  sx={{ 
                    fontSize: '1rem',
                    fontWeight: 600,
                    color: theme.palette.text.primary
                  }}
                >
                  {t('code')}
                </RTLTableCell>
                <RTLTableCell 
                  isRtl={isRtl} 
                  isHeader
                  sx={{ 
                    fontSize: '1rem',
                    fontWeight: 600,
                    color: theme.palette.text.primary
                  }}
                >
                  {t('created_at')}
                </RTLTableCell>
                {/* Always show actions column in development, or if user has permission */}
                {(process.env.NODE_ENV === 'development' || permissions.canUpdate || permissions.canDelete) && (
                  <RTLTableCell 
                    isRtl={isRtl} 
                    align={isRtl ? "right" : "right"} 
                    isHeader
                    sx={{ 
                      fontSize: '1rem',
                      fontWeight: 600,
                      color: theme.palette.text.primary,
                      textAlign: isRtl ? 'right' : 'right',
                    }}
                  >
                    {t('common:actions')}
                  </RTLTableCell>
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <RTLTableCell 
                    colSpan={(process.env.NODE_ENV === 'development' || permissions.canUpdate || permissions.canDelete) ? 5 : 4} 
                    align="center" 
                    sx={{ py: 3 }} 
                    isRtl={isRtl}
                  >
                    <CircularProgress size={30} />
                  </RTLTableCell>
                </TableRow>
              ) : provinces.length === 0 ? (
                <TableRow>
                  <RTLTableCell 
                    colSpan={(process.env.NODE_ENV === 'development' || permissions.canUpdate || permissions.canDelete) ? 5 : 4} 
                    align="center" 
                    sx={{ py: 3 }} 
                    isRtl={isRtl}
                  >
                    {t('common:no_records')}
                  </RTLTableCell>
                </TableRow>
              ) : (
                provinces.map((province, index) => (
                  <TableRow 
                    key={province.id} 
                    hover
                    sx={{ 
                      '&:hover': { backgroundColor: 'grey.50' },
                      '& td': { 
                        py: 1.5, // Increased vertical padding for better spacing
                      }
                    }}
                  >
                    <RTLTableCell isRtl={isRtl} align={isRtl ? "right" : "center"}>
                      <Typography variant="body2" sx={{ 
                        fontWeight: 500,
                        textAlign: isRtl ? 'right' : 'center' 
                      }}>
                        {(page - 1) * limit + index + 1}
                      </Typography>
                    </RTLTableCell>
                    <RTLTableCell isRtl={isRtl} sx={{ fontWeight: 500 }}>
                      {province.name}
                    </RTLTableCell>
                    <RTLTableCell isRtl={isRtl}>
                      {province.code}
                    </RTLTableCell>
                    <RTLTableCell isRtl={isRtl}>
                      {province.created_at ? new Date(province.created_at).toLocaleDateString() : '-'}
                    </RTLTableCell>
                    {(process.env.NODE_ENV === 'development' || permissions.canUpdate || permissions.canDelete) && (
                      <RTLTableCell isRtl={isRtl} align={isRtl ? "left" : "right"}>
                        <Box sx={{ display: 'flex', justifyContent: isRtl ? 'flex-start' : 'flex-end', gap: 1 }}>
                          {(process.env.NODE_ENV === 'development' || permissions.canUpdate) && (
                            <Tooltip title={t('common:edit')} placement={isRtl ? "right" : "left"}>
                              <IconButton
                        size="small"
                                onClick={() => handleOpenEditDialog(province.id)}
                        color="primary"
                                sx={{
                                  '&:hover': {
                                    backgroundColor: 'rgba(25, 118, 210, 0.08)', // Consistent primary light hover color
                                  }
                                }}
                              >
                                <EditIcon fontSize="small" />
                      </IconButton>
                            </Tooltip>
                          )}
                          {(process.env.NODE_ENV === 'development' || permissions.canDelete) && (
                            <Tooltip title={t('common:delete')} placement={isRtl ? "right" : "left"}>
                      <IconButton
                        size="small"
                                onClick={() => handleDelete(province.id)}
                        color="error"
                                sx={{
                                  '&:hover': {
                                    backgroundColor: 'rgba(211, 47, 47, 0.08)', // Consistent error light hover color
                                  }
                                }}
                              >
                                <DeleteIcon fontSize="small" />
                      </IconButton>
                            </Tooltip>
                          )}
                        </Box>
                      </RTLTableCell>
                    )}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        
        {/* Pagination */}
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          p: 2,
          flexDirection: isRtl ? 'row-reverse' : 'row' 
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography 
              variant="body2" 
              color="text.secondary"
              sx={{ 
                mr: isRtl ? 0 : 2, 
                ml: isRtl ? 2 : 0,
                whiteSpace: 'nowrap'
              }}
            >
              {t('common:rows_per_page')}:
            </Typography>
            <Select
              value={limit}
              onChange={handleLimitChange}
              size="small"
              sx={{ 
                minWidth: 70,
                '& .MuiSelect-select': {
                  px: 2
                },
                ...(isRtl && {
                  '& .MuiSelect-select': {
                    paddingRight: '32px',
                    textAlign: 'right',
                    paddingLeft: '14px',
                  },
                  '& .MuiSelect-icon': {
                    right: 7,
                    left: 'auto',
                  },
                }),
              }}
              MenuProps={{
                disableScrollLock: true,
                anchorOrigin: {
                  vertical: 'top',
                  horizontal: isRtl ? 'right' : 'left',
                },
                transformOrigin: {
                  vertical: 'top',
                  horizontal: isRtl ? 'right' : 'left',
                },
              }}
            >
              <MenuItem value={5}>5</MenuItem>
              <MenuItem value={10}>10</MenuItem>
              <MenuItem value={25}>25</MenuItem>
              <MenuItem value={50}>50</MenuItem>
            </Select>
          </Box>
          
          {/* Make sure we always show pagination even if total is just 1 page */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
              {t('common:showing')} {(page - 1) * limit + 1}-
              {Math.min(page * limit, data?.count || 0)} {t('common:of')} {data?.count || 0}
            </Typography>
            <Pagination
              count={Math.max(totalPages, 1)}  
          page={page}
              onChange={handlePageChange}
              color="primary"
              dir={isRtl ? 'rtl' : 'ltr'}
              shape="rounded"
              size="small"
              showFirstButton
              showLastButton
            />
          </Box>
        </Box>
      </Paper>

      {/* Create Dialog */}
      <Dialog 
        open={createDialogOpen} 
        onClose={handleCloseCreateDialog}
        fullWidth
        maxWidth="sm"
        dir={isRtl ? 'rtl' : 'ltr'}
        PaperProps={{
          sx: {
            borderRadius: '8px',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.08)',
            maxWidth: '600px',
            width: '100%',
            mx: 'auto',
          }
        }}
      >
        <RTLDialogTitle isRtl={isRtl} sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          fontWeight: 'bold',
          pb: 1,
          pt: 2,
          height: '60px',
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}>
          <Typography variant="h5">{t('create_title')}</Typography>
          <IconButton onClick={handleCloseCreateDialog} size="small">
            <CloseIcon />
          </IconButton>
        </RTLDialogTitle>
        
        <RTLDialogContent isRtl={isRtl} sx={{ pt: 3 }}>
          <Box component="form" id="create-form" onSubmit={handleCreateSubmit}>
            <RTLTextField
              fullWidth
              name="name"
              label={t('name')}
              value={formData.name}
              onChange={handleInputChange}
              error={!!errors.name}
              helperText={errors.name}
              required
              margin="normal"
              isRtl={isRtl}
              InputProps={{
                dir: isRtl ? 'rtl' : 'ltr',
              }}
              FormHelperTextProps={{
                style: { textAlign: isRtl ? 'right' : 'left' }
              }}
            />
            
            <RTLTextField
              fullWidth
              name="code"
              label={t('code')}
              value={formData.code}
              onChange={handleInputChange}
              error={!!errors.code}
              helperText={errors.code}
              required
              margin="normal"
              isRtl={isRtl}
              InputProps={{
                dir: isRtl ? 'rtl' : 'ltr',
              }}
              FormHelperTextProps={{
                style: { textAlign: isRtl ? 'right' : 'left' }
              }}
            />
          </Box>
        </RTLDialogContent>
        
        <RTLDialogActions isRtl={isRtl} sx={{ 
          px: 3, 
          pb: 3, 
          gap: 1,
          borderTop: '1px solid',
          borderColor: 'divider',
          pt: 2
        }}>
          <RTLButton 
            variant="outlined"
            onClick={handleCloseCreateDialog}
            isRtl={isRtl}
          >
            {t('common:cancel')}
          </RTLButton>
          <RTLButton 
            variant="contained"
            type="submit"
            form="create-form"
            disabled={isCreating}
            isRtl={isRtl}
          >
            {isCreating ? (
              <CircularProgress size={24} />
            ) : (
              t('common:save')
            )}
          </RTLButton>
        </RTLDialogActions>
      </Dialog>
      
      {/* Edit Dialog */}
      <Dialog 
        open={editDialogOpen} 
        onClose={handleCloseEditDialog}
        fullWidth
        maxWidth="sm"
        dir={isRtl ? 'rtl' : 'ltr'}
        PaperProps={{
          sx: {
            borderRadius: '8px',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.08)',
            maxWidth: '600px',
            width: '100%',
            mx: 'auto',
          }
        }}
      >
        <RTLDialogTitle isRtl={isRtl} sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          fontWeight: 'bold',
          pb: 1,
          pt: 2,
          height: '60px',
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}>
          <Typography variant="h5">{t('edit_title')}</Typography>
          <IconButton onClick={handleCloseEditDialog} size="small">
            <CloseIcon />
          </IconButton>
        </RTLDialogTitle>
        
        <RTLDialogContent isRtl={isRtl} sx={{ pt: 3 }}>
          {isLoadingProvince ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', my: 3 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Box component="form" id="edit-form" onSubmit={handleEditSubmit}>
              <RTLTextField
                fullWidth
                name="name"
                label={t('name')}
                value={formData.name}
                onChange={handleInputChange}
                error={!!errors.name}
                helperText={errors.name}
                required
                margin="normal"
                isRtl={isRtl}
                InputProps={{
                  dir: isRtl ? 'rtl' : 'ltr',
                }}
                FormHelperTextProps={{
                  style: { textAlign: isRtl ? 'right' : 'left' }
                }}
              />
              
              <RTLTextField
                fullWidth
                name="code"
                label={t('code')}
                value={formData.code}
                onChange={handleInputChange}
                error={!!errors.code}
                helperText={errors.code}
                required
                margin="normal"
                isRtl={isRtl}
                InputProps={{
                  dir: isRtl ? 'rtl' : 'ltr',
                }}
                FormHelperTextProps={{
                  style: { textAlign: isRtl ? 'right' : 'left' }
                }}
              />
            </Box>
          )}
        </RTLDialogContent>
        
        <RTLDialogActions isRtl={isRtl} sx={{ 
          px: 3, 
          pb: 3, 
          gap: 1,
          borderTop: '1px solid',
          borderColor: 'divider',
          pt: 2
        }}>
          <RTLButton 
            variant="outlined"
            onClick={handleCloseEditDialog}
            isRtl={isRtl}
          >
            {t('common:cancel')}
          </RTLButton>
          <RTLButton 
            variant="contained"
            type="submit"
            form="edit-form"
            disabled={isUpdating || isLoadingProvince}
            isRtl={isRtl}
          >
            {isUpdating ? (
              <CircularProgress size={24} />
            ) : (
              t('common:save')
            )}
          </RTLButton>
        </RTLDialogActions>
      </Dialog>
      
      {/* Delete confirmation dialog */}
      <ConfirmDialog message={t('delete_confirm_text')} />

      {/* Permission error snackbar */}
      <Snackbar
        open={showPermissionError}
        autoHideDuration={6000}
        onClose={handleClosePermissionError}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleClosePermissionError} 
          severity="error" 
          sx={{ width: '100%', textAlign: isRtl ? 'right' : 'left' }}
        >
          {permissionError}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ProvinceList; 